import { useEffect, useRef } from "react";
import { Gantt } from "libs/dhtmlx-gantt-shim";
import "dhtmlx-gantt/codebase/dhtmlxgantt.css";
// import { formatDate } from "pages/schedule/schedule-page/procurment-schedule/procurment-schedule";

interface Task {
  id: string | number;
  text?: string;
  type?: string | number;
  risk_level?: string;
  activity_count?: number;
  $source?: (string | number)[];
  $target?: (string | number)[];
  workflow_status?: "WF_NOT_STARTED" | "IN_PROGRESS" | "WF_COMPLETED";

  material_schedule_links?: Array<any>;
}

const formatDate = (date: string | null | undefined): string | null => {
  if (date !== null && date !== undefined) {
    const formattedDate = new Date(date);
    return `${String(formattedDate.getMonth() + 1).padStart(2, "0")}-${String(
      formattedDate.getDate()
    ).padStart(2, "0")}-${formattedDate.getFullYear()}`;
  }
  return null;
};

interface GanttViewProps {
  tasks: {
    data: Task[];
    links: { id: string; source: string; target: string; type: string }[];
  };
  zoom: string;
  togglePanel: () => void;
}

export default function GanttView({
  tasks,
  zoom,
  togglePanel
}: Readonly<GanttViewProps>) {
  const container = useRef<HTMLDivElement>(null);

  const getTaskColor = (task: Task): string => {
    if (task.risk_level?.toString() === "HIGH") {
      return "red";
    }
    return "white";
  };

  useEffect(() => {
    const gantt = Gantt.getGanttInstance();
    gantt.skin = "broadway";
    gantt.config.csp = true;
    gantt.config.readonly = true;
    gantt.plugins({
      tooltip: true,
      marker: true
    });
    gantt.config.show_markers = true;
    gantt.config.tooltip_timeout = 500;
    gantt.config.columns = [
      {
        name: "",
        label: "",
        width: "",
        min_width: 25,
        max_width: 25,
        template(task: Task) {
          const color = getTaskColor(task);
          return `<div style="background-color: ${color}; width: 100%; height: 100%; margin-left:-11px"></div>`;
        }
      },
      {
        name: "text",
        label: "",
        tree: true,
        width: "*",
        min_width: 400,
        max_width: 800,
        template(task: Task) {
          if (task.type === "S")
            return `<div class="icon-text">
            <svg width="12" height="12" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.49984 3.08333V1.33333C2.49984 1.01117 2.76101 0.75 3.08317 0.75H6.82479L7.99145 1.91667H11.2498C11.572 1.91667 11.8332 2.17784 11.8332 2.5V8.33333C11.8332 8.65551 11.572 8.91667 11.2498 8.91667H9.49984V10.6667C9.49984 10.9888 9.23868 11.25 8.9165 11.25H0.749837C0.427674 11.25 0.166504 10.9888 0.166504 10.6667V3.66667C0.166504 3.3445 0.427674 3.08333 0.749837 3.08333H2.49984ZM2.49984 4.25H1.33317V10.0833H8.33317V8.91667H2.49984V4.25ZM3.6665 1.91667V7.75H10.6665V3.08333H7.50822L6.34155 1.91667H3.6665Z"
                fill="var(--ciq-primary-900)" fillOpacity="0.9" />
            </svg>
            <label>${task.text}</label>
          </div>`;
          if (task.type === "M")
            return `<div class="icon-text">
            <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.7153 8.86654L11.4166 9.28729C11.5546 9.37019 11.5994 9.54933 11.5166 9.68746C11.4919 9.72853 11.4576 9.76289 11.4166 9.7875L6.30015 12.8573C6.11541 12.9682 5.88464 12.9682 5.6999 12.8573L0.583546 9.7875C0.445419 9.70461 0.400624 9.52547 0.483504 9.38733C0.508133 9.34627 0.542491 9.31191 0.583546 9.28729L1.28477 8.86654L6.00002 11.6957L10.7153 8.86654ZM10.7153 6.12487L11.4166 6.54563C11.5546 6.62852 11.5994 6.80766 11.5166 6.94579C11.4919 6.98686 11.4576 7.02122 11.4166 7.04584L6.00002 10.2957L0.583546 7.04584C0.445419 6.96294 0.400624 6.7838 0.483504 6.64567C0.508133 6.6046 0.542491 6.57024 0.583546 6.54563L1.28477 6.12487L6.00002 8.95404L10.7153 6.12487ZM6.30015 0.763301L11.4166 3.83313C11.5546 3.916 11.5994 4.09516 11.5166 4.23329C11.4919 4.27434 11.4576 4.3087 11.4166 4.33333L6.00002 7.5832L0.583546 4.33333C0.445419 4.25046 0.400624 4.0713 0.483504 3.93317C0.508133 3.89211 0.542491 3.85776 0.583546 3.83313L5.6999 0.763301C5.88464 0.652467 6.11541 0.652467 6.30015 0.763301ZM6.00002 1.94379L2.4343 4.08323L6.00002 6.22269L9.56577 4.08323L6.00002 1.94379Z"
                fill="var(--ciq-teal-700)"  fillOpacity="0.9" />
            </svg>
            <label>${task.text}</label>
          </div>`;
          if (task.type === "V")
            return `<div class="icon-text">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" fill="var(--ciq-orange-600)"/>
            <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" stroke="white"/>
            <g clip-path="url(#clip0_918_36903)">
              <path d="M16.6406 15.3694C16.388 14.771 16.0213 14.2274 15.5611 13.769C15.1023 13.3092 14.5589 12.9427 13.9607 12.6895C13.9553 12.6868 13.95 12.6855 13.9446 12.6828C14.779 12.0801 15.3214 11.0985 15.3214 9.99086C15.3214 8.15604 13.8348 6.66943 12 6.66943C10.1652 6.66943 8.67855 8.15604 8.67855 9.99086C8.67855 11.0985 9.22096 12.0801 10.0553 12.6842C10.05 12.6868 10.0446 12.6882 10.0393 12.6909C9.43927 12.944 8.90087 13.3069 8.43882 13.7703C7.97906 14.2291 7.61249 14.7726 7.35936 15.3708C7.11068 15.9564 6.97656 16.5842 6.96427 17.2203C6.96391 17.2346 6.96642 17.2488 6.97164 17.2622C6.97686 17.2755 6.9847 17.2876 6.99469 17.2978C7.00467 17.3081 7.0166 17.3162 7.02978 17.3217C7.04296 17.3273 7.05711 17.3302 7.07141 17.3301H7.87498C7.93391 17.3301 7.98078 17.2833 7.98212 17.2257C8.00891 16.1918 8.42409 15.2235 9.15802 14.4895C9.91739 13.7301 10.9259 13.3123 12 13.3123C13.0741 13.3123 14.0826 13.7301 14.8419 14.4895C15.5759 15.2235 15.9911 16.1918 16.0178 17.2257C16.0192 17.2846 16.0661 17.3301 16.125 17.3301H16.9286C16.9429 17.3302 16.957 17.3273 16.9702 17.3217C16.9834 17.3162 16.9953 17.3081 17.0053 17.2978C17.0153 17.2876 17.0231 17.2755 17.0283 17.2622C17.0335 17.2488 17.0361 17.2346 17.0357 17.2203C17.0223 16.5801 16.8897 15.9574 16.6406 15.3694ZM12 12.2944C11.3852 12.2944 10.8067 12.0547 10.3714 11.6194C9.93614 11.1842 9.69641 10.6056 9.69641 9.99086C9.69641 9.37613 9.93614 8.79756 10.3714 8.36229C10.8067 7.92702 11.3852 7.68729 12 7.68729C12.6147 7.68729 13.1933 7.92702 13.6286 8.36229C14.0638 8.79756 14.3036 9.37613 14.3036 9.99086C14.3036 10.6056 14.0638 11.1842 13.6286 11.6194C13.1933 12.0547 12.6147 12.2944 12 12.2944Z" fill="white"/>
            </g>
            <defs>
              <clipPath id="clip0_918_36903">
              <rect width="12" height="12" fill="white" transform="translate(6 6)"/>
              </clipPath>
            </defs>
          </svg>
            <label>${task.text}</label>
          </div>`;
          return task.text;
        }
      }
    ];

    gantt.addMarker({
      start_date: new Date(),
      css: "bg-ciq-primary-900 text-[10px]",
      text: "Today"
    });

    window.addEventListener("click", function (e) {
      const filterCheckbox = (e.target as HTMLElement).closest(
        ".filterCheckbox"
      );
      if (filterCheckbox) {
        gantt.render();
      }
    });
    function hasPriority(parent: string | number, priority: string) {
      if (
        (gantt.getTask(parent).type === "V" ||
          gantt.getTask(parent).type === "S" ||
          gantt.getTask(parent).type === "M") &&
        gantt.getTask(parent).risk_level === priority
      ) {
        return true;
      }

      const child = gantt.getChildren(parent);
      for (let i = 0; i < child.length; i += 1) {
        if (hasPriority(child[i], priority)) {
          return true;
        }
      }
      return false;
    }
    function filterLogic(id: string | number) {
      const filterCheckboxes = document.querySelectorAll(
        ".filterCheckbox input"
      );
      let applyFilter = false;
      let returnValue = false;

      for (let i = 0; i < filterCheckboxes.length; i += 1) {
        const filterCheckbox = filterCheckboxes[i];
        if ((filterCheckbox as HTMLInputElement).checked) {
          applyFilter = true;
          if (hasPriority(id, (filterCheckbox as HTMLInputElement).name)) {
            returnValue = true;
            break;
          }
        }
      }
      if (applyFilter) {
        return returnValue;
      }
      return true;
    }

    gantt.attachEvent("onBeforeTaskDisplay", function (id) {
      return filterLogic(id);
    });

    gantt.attachEvent(
      "onBeforeRollupTaskDisplay",

      function (taskId, task) {
        if (
          (task.type?.toString() === "SS" || task.type?.toString() === "MS") &&
          task.risk_level?.toString() === "HIGH"
        )
          task.text = "";
        return true;
      }
    );

    function buttonHtml(
      action: string,
      data: { activity_count: number; material_schedule_links: Array<any> }
    ) {
      const { activity_count: count, material_schedule_links: list = [] } =
        data;
      return `<div style="padding-left: 0px;display: flex;align-items: center;padding-top: 3px;">
                <div style="width: 15px; height: 1px; background-color: black;"></div>
                <span class='activity-box' data-action="${action}" name='${count}' aria-label="
                    <h3>Activity Info</h3>
                    <b>Count: </b> ${count}
                      ${list.map(
                        (l: any) =>
                          `<br />
                          <b>Name : </b> ${l.text} </b> <br />
                          <b>Start Date : </b> ${formatDate(
                            l.start_date
                          )} <br />
                          <b>End Date : </b> ${formatDate(l.end_date)}
                        <br />`
                      )}
                  " />
            </div>`;
    }
    gantt.templates.rightside_text = (start, end, task) => {
      if (
        (task.type?.toString() === "S" || task.type?.toString() === "M") &&
        // task.lastMilestone
        task.activity_count > 0
      ) {
        return buttonHtml("openPanel", task as any);
      }
      return "";
    };

    gantt.attachEvent("onTaskClick", function (id, e) {
      const actionButton =
        e?.target instanceof HTMLElement
          ? e.target.closest(".activity-box")
          : null;
      if (actionButton) {
        const actionType = actionButton.getAttribute("data-action");
        if (actionType === "openPanel") {
          togglePanel();
          return false;
        }
        return true;
      }

      const timelineClick =
        e?.target instanceof HTMLElement
          ? gantt.utils.dom.closest(e.target, ".gantt_task_line")
          : null;
      if (timelineClick) {
        togglePanel();
        return false;
      }
      return true;
    });

    gantt.attachEvent("onGanttReady", function () {
      const { tooltips } = gantt.ext;
      tooltips.tooltipFor({
        selector: ".activity-box",
        html() {
          const spanElement = document.querySelector(
            'span.activity-box[data-action="openPanel"]'
          );
          if (spanElement) {
            const obj = spanElement.getAttribute("aria-label") as any;
            return obj;
            // const actCount = spanElement.getAttribute("name");
            // const activityText = actCount === "1" ? "activity" : "activities";
            // return `${actCount} ${activityText}`;
          }
          return "";
        }
      });
    });

    let linkTasks: { [key: string]: boolean } = {};

    gantt.attachEvent("onMouseMove", function (id: string, e: MouseEvent) {
      const previousLinkTasks = linkTasks;
      let currentTask: Task | undefined;
      linkTasks = {};
      if (
        id &&
        e.target instanceof HTMLElement &&
        e.target.closest(".gantt_task_line")
      ) {
        currentTask = gantt.getTask(id);
        currentTask.$source?.forEach((item) => {
          const taskId = gantt.getLink(item).target;
          linkTasks[taskId] = true;
        });
        currentTask.$target?.forEach((item) => {
          const taskId = gantt.getLink(item).source;
          linkTasks[taskId] = true;
        });
      }

      Object.keys(linkTasks).forEach((i) => {
        // i comes from Object.keys, so hasOwnProperty is always true; false branch unreachable
        /* v8 ignore next */
        if (Object.hasOwn(linkTasks, i)) {
          gantt.refreshTask(i);
        }
      });
      Object.keys(previousLinkTasks).forEach((i) => {
        // i comes from Object.keys, so hasOwnProperty is always true; false branch unreachable
        /* v8 ignore next */
        if (Object.hasOwn(previousLinkTasks, i)) {
          gantt.refreshTask(i);
        }
      });
    });

    const getWorkflowClass = (workflow_status?: any) => {
      let workflow = "";
      switch (workflow_status) {
        case "WF_COMPLETED":
          workflow = "gantt_task_completed";
          break;
        case "IN_PROGRESS":
          workflow = "gantt_task_inprogress";
          break;
        default:
          workflow = "gantt_task_not_started";
          break;
      }
      return workflow;
    };

    gantt.templates.task_class = function (start, end, task: Task) {
      if (linkTasks[task.id]) {
        return "gantt_task_background";
      }
      if (
        (task.type?.toString() === "SS" || task.type?.toString() === "MS") &&
        task.risk_level?.toString() === "HIGH"
      ) {
        return "risk_subSubmittal";
      }
      if (task.type?.toString() === "V") {
        return "vendor";
      }
      if (task.type?.toString() === "S") {
        const workflow = getWorkflowClass(task.workflow_status);
        const riskClass =
          task.risk_level === "HIGH" ? "gantt_task_high_risk" : "";
        return `submittal ${workflow} ${riskClass}`;
      }
      if (task.type?.toString() === "SS") {
        return "subSubmittal";
      }
      if (task.type?.toString() === "MS") {
        return "subMaterial";
      }
      if (task.type?.toString() === "M") {
        const workflow = getWorkflowClass(task.workflow_status);
        const riskClass =
          task.risk_level === "HIGH" ? "gantt_task_high_risk" : "";
        return `material ${workflow} ${riskClass}`;
      }

      return "";
    };

    gantt.templates.tooltip_text = function (start, end, task) {
      if (task.type === "V") {
        return `
        <b>Task: </b> ${task.text}<br/>
        <b>Start Date: </b> ${formatDate(task.start_date?.toISOString())}<br/>
        <b>End Date: </b> ${formatDate(task.end_date?.toISOString())}<br/>
        ${task.risk_level && `<b>Risk: </b>${task.risk_level}`}`;
      }

      if (task.type === "S" || task.type === "M") {
        return `
        <b>${task.type === "S" ? "Submittal: " : "Material: "} </b> ${
          task.text
        }<br/>
        <b>Start Date: </b>${formatDate(
          task.start_date?.toISOString()
        )}<br/><b>End Date: </b> ${formatDate(task.end_date?.toISOString())}
        <br/>
        <b>Actual Float: </b>  ${task.actual_float || 0} <br /> 
        ${
          task.workflow_status !== "WF_COMPLETED"
            ? `<b>Risk: </b> ${
                task.risk_level === null ? "NONE" : task.risk_level
              }<br/>
          ${
            task.risk_assessment !== "" && task.risk_assessment !== null
              ? `<div style="max-width:240px; white-space:break-spaces;"><b>Risk Assessment: </b> ${task.risk_assessment} </div>`
              : ""
          }`
            : ""
        }`;
      }

      // if (task.type === "SS" || task.type === "MS") {
      //   return `
      //   <b>Task:</b> ${task.hoverText.text}<br/>
      //   <b>Start Date:</b> ${task.hoverText.start_date}<br/><b>End Date:</b> ${
      //     task.hoverText.end_date
      //   }<br/>
      //   <b>Risk:</b> ${
      //     task.hoverText.risk_level === null
      //       ? "NONE"
      //       : task.hoverText.risk_level
      //   }<br/>
      //   ${
      //     task.hoverText.risk_assessment !== ""
      //       ? `<b>Risk Assessment:</b> ${task.hoverText.risk_assessment}`
      //       : ""
      //   }`;
      // }
      return "";
    };

    gantt.init(container.current!);
    gantt.parse(tasks);

    gantt.sort((a: Task, b: Task) => {
      if (a.type !== "V" || b.type !== "V" || !a.text || !b.text) return 0;
      return a.text > b?.text ? 1 : -1;
    });

    function initZoom() {
      gantt.ext.zoom.init({
        levels: [
          {
            name: "Days",
            scale_height: 50,
            min_column_width: 80,
            scales: [
              { unit: "week", step: 1, format: "%Y" },
              { unit: "day", step: 1, format: "%d %M" }
            ]
          },
          {
            name: "Weeks",
            scale_height: 50,
            min_column_width: 50,
            scales: [
              {
                unit: "week",
                step: 1,
                format(date) {
                  const dateToStr = gantt.date.date_to_str("%d %M");
                  const endDate = gantt.date.add(date, -6, "day");
                  const weekNum = gantt.date.date_to_str("%W")(date);
                  return `#${weekNum}, ${dateToStr(date)} - ${dateToStr(
                    endDate
                  )}`;
                }
              },
              { unit: "day", step: 1, format: "%j %D" }
            ]
          },
          {
            name: "Months",
            scale_height: 50,
            min_column_width: 120,
            scales: [
              { unit: "month", format: "%F, %Y" },
              { unit: "week", format: "Week #%W" }
            ]
          },
          {
            name: "Quarter",
            scale_height: 50,
            min_column_width: 90,
            scales: [
              { unit: "month", step: 1, format: "%M" },
              {
                unit: "quarter",
                step: 1,
                format(date) {
                  const dateToStr = gantt.date.date_to_str("%M %Y");
                  const endDate = gantt.date.add(
                    gantt.date.add(date, 3, "month"),
                    -1,
                    "day"
                  );
                  return `${dateToStr(date)} - ${dateToStr(endDate)}`;
                }
              }
            ]
          },
          {
            name: "Year",
            scale_height: 50,
            min_column_width: 30,
            scales: [{ unit: "year", step: 1, format: "%Y" }]
          }
        ]
      });
    }

    function setZoom(value: string) {
      if (!gantt.ext.zoom.getLevels()) {
        initZoom();
      }
      gantt.ext.zoom.setLevel(value);
    }

    setZoom(zoom);

    return () => {
      gantt.destructor();
      if (container.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        container.current.innerHTML = "";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, zoom]);

  return <div ref={container} style={{ width: "100%", height: "100%" }} />;
}
